import { InvalidTimeframe } from '../error';
export class SortedTimeframeCollection {
    constructor(timeframes = []) {
        this._timeframes = [...timeframes];
        this.sortAscending();
    }
    get timeframes() {
        return this._timeframes;
    }
    /**
     * Inserts a timeframe and merges overlapping timeframes.
     * @param timeframe The timeframe to insert.
     * @param preserveAdjacentTimeframes If true, adjacent timeframes won't be merged.
     * For example, `{ start: 0, end: 1 }` would not be merged with `{ start: 1, end: 2 }`.
     */
    insert(timeframe, preserveAdjacentTimeframes = false) {
        var _a, _b;
        validate(timeframe);
        if (this.isEmpty()) {
            this.timeframes.push(timeframe);
            return { merged: [], final: timeframe };
        }
        const enclosingTimeframe = this.timeframes.find(x => encloses(x, timeframe));
        if (enclosingTimeframe) {
            return { merged: [enclosingTimeframe], final: enclosingTimeframe };
        }
        // adjust new timeframe to enclose overlapping timeframes
        const timeframeOverlappingStart = preserveAdjacentTimeframes
            ? this.timeframes.find(x => containsExclusiveEnd(x, timeframe.start))
            : this.timeframes.find(x => containsInclusive(x, timeframe.start));
        const timeframeOverlappingEnd = preserveAdjacentTimeframes
            ? this.timeframes.find(x => containsExclusiveStart(x, timeframe.end))
            : this.timeframes.find(x => containsInclusive(x, timeframe.end));
        const newTimeframe = {
            start: (_a = timeframeOverlappingStart === null || timeframeOverlappingStart === void 0 ? void 0 : timeframeOverlappingStart.start) !== null && _a !== void 0 ? _a : timeframe.start,
            end: (_b = timeframeOverlappingEnd === null || timeframeOverlappingEnd === void 0 ? void 0 : timeframeOverlappingEnd.end) !== null && _b !== void 0 ? _b : timeframe.end,
        };
        // remove timeframes enclosed by new timeframe
        const mergedTimeframes = this.timeframes.filter(x => encloses(newTimeframe, x));
        this.removeTimeframes(mergedTimeframes);
        this.timeframes.push(newTimeframe);
        this.sortAscending();
        return { merged: mergedTimeframes, final: newTimeframe };
    }
    /**
     * Removes timeframes.
     * @param timeframes The timeframes to remove.
     */
    remove(...timeframes) {
        timeframes.forEach(validate);
        if (this.isEmpty() || timeframes.length === 0) {
            return { deleted: [], updated: [] };
        }
        // merge and sort timeframes
        const collection = new SortedTimeframeCollection();
        timeframes.forEach(x => collection.insert(x));
        timeframes = collection.timeframes;
        // remove timeframes enclosed by timeframes
        const deletedTimeframes = timeframes.flatMap(timeframe => this.timeframes.filter(x => encloses(timeframe, x)));
        this.removeTimeframes(deletedTimeframes);
        // trim overlapping timeframes
        const updatedTimeframes = [];
        timeframes.forEach(timeframe => {
            const timeframeOverlappingStart = this.timeframes.find(x => containsExclusive(x, timeframe.start));
            const timeframeOverlappingEnd = this.timeframes.find(x => containsExclusive(x, timeframe.end));
            if (timeframeOverlappingStart) {
                this.removeTimeframes([timeframeOverlappingStart]);
                const newTimeframe = { start: timeframeOverlappingStart.start, end: timeframe.start };
                this.timeframes.push(newTimeframe);
                // add or update updated timeframe
                const existingUpdatedTimeframe = updatedTimeframes.find(x => encloses(x.before, timeframeOverlappingStart));
                if (existingUpdatedTimeframe) {
                    existingUpdatedTimeframe.after = existingUpdatedTimeframe.after.filter(x => !encloses(x, newTimeframe));
                    existingUpdatedTimeframe.after.push(newTimeframe);
                }
                else {
                    updatedTimeframes.push({ before: timeframeOverlappingStart, after: [newTimeframe] });
                }
            }
            if (timeframeOverlappingEnd) {
                this.removeTimeframes([timeframeOverlappingEnd]);
                const newTimeframe = { start: timeframe.end, end: timeframeOverlappingEnd.end };
                this.timeframes.push(newTimeframe);
                // add or update updated timeframe
                const existingUpdatedTimeframe = updatedTimeframes.find(x => encloses(x.before, timeframeOverlappingEnd));
                if (existingUpdatedTimeframe) {
                    existingUpdatedTimeframe.after = existingUpdatedTimeframe.after.filter(x => !encloses(x, newTimeframe));
                    existingUpdatedTimeframe.after.push(newTimeframe);
                }
                else {
                    updatedTimeframes.push({ before: timeframeOverlappingEnd, after: [newTimeframe] });
                }
            }
        });
        this.sortAscending();
        return { deleted: deletedTimeframes, updated: updatedTimeframes };
    }
    /**
     * Determines whether this collection contains a second of the day.
     * @param secondOfDay The second of the day.
     */
    contains(secondOfDay) {
        if (secondOfDay < 0 || 86399 < secondOfDay) {
            throw new RangeError(`secondOfDay (${secondOfDay}) is outside the acceptable range 0 to 86399`);
        }
        return this.timeframes.some(x => containsExclusiveEnd(x, secondOfDay));
    }
    /**
     * Returns timeframes that exist in this collection but not in the other collection.
     * @param collection The other collection.
     */
    difference(collection) {
        const thisCollection = new SortedTimeframeCollection(this.timeframes);
        thisCollection.remove(...collection.timeframes);
        return thisCollection.timeframes;
    }
    /**
     * Determines whether this collection includes a timeframe exactly.
     * @param timeframe The exact timeframe.
     */
    has(timeframe) {
        return includes(this.timeframes, timeframe);
    }
    isEmpty() {
        return this.timeframes.length === 0;
    }
    removeTimeframes(timeframes) {
        this._timeframes = this.timeframes.filter(x => !includes(timeframes, x));
    }
    sortAscending() {
        this._timeframes.sort((a, b) => {
            if (a.start === b.start) {
                return 0;
            }
            return a.start < b.start ? -1 : 1;
        });
    }
}
function containsExclusive(timeframe, secondOfDay) {
    return timeframe.start < secondOfDay && secondOfDay < timeframe.end;
}
function containsExclusiveEnd(timeframe, secondOfDay) {
    return timeframe.start <= secondOfDay && secondOfDay < timeframe.end;
}
function containsExclusiveStart(timeframe, secondOfDay) {
    return timeframe.start < secondOfDay && secondOfDay <= timeframe.end;
}
function containsInclusive(timeframe, secondOfDay) {
    return timeframe.start <= secondOfDay && secondOfDay <= timeframe.end;
}
function encloses(timeframe, other) {
    return timeframe.start <= other.start && other.end <= timeframe.end;
}
function equals(timeframe, other) {
    return timeframe.start === other.start && timeframe.end === other.end;
}
function includes(timeframes, timeframe) {
    return timeframes.some(x => equals(x, timeframe));
}
function validate(timeframe) {
    if (timeframe.end <= timeframe.start) {
        throw new InvalidTimeframe(timeframe);
    }
}
